// components/charts/AreaChart.tsx
import React, { useMemo, useCallback } from "react";
import {
  ResponsiveContainer,
  AreaChart as RechartsAreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

interface AreaChartProps {
  title: string;
  description?: string;
  data: any[];
  xAxisKey: string;
  yAxisKey: string;
  areaColor?: string;
  fillOpacity?: number;
  showGrid?: boolean;
  height?: number;
  loading?: boolean;
  error?: string;
  strokeWidth?: number;
  gradient?: boolean;
}

const AreaChart: React.FC<AreaChartProps> = ({
  title,
  description,
  data,
  xAxisKey,
  yAxisKey,
  areaColor = "#3b82f6",
  fillOpacity = 0.3,
  showGrid = true,
  height = 400,
  loading = false,
  error,
  strokeWidth = 2,
  gradient = true,
}) => {
  const gradientId = useMemo(
    () => `areaGradient-${Math.random().toString(36).substr(2, 9)}`,
    []
  );

  const CustomTooltip = useCallback(
    ({ active, payload, label }: any) => {
      if (!active || !payload?.length) return null;

      return (
        <div className="custom-tooltip">
          <p className="tooltip-title">{label}</p>
          <div className="tooltip-item">
            <div className="tooltip-label">
              <div
                className="tooltip-dot"
                style={{ backgroundColor: areaColor }}
              />
              <span>{payload[0].name || yAxisKey}</span>
            </div>
            <span className="tooltip-value">
              {typeof payload[0].value === "number"
                ? payload[0].value.toLocaleString()
                : payload[0].value}
            </span>
          </div>
        </div>
      );
    },
    [areaColor, yAxisKey]
  );

  const chartElements = useMemo(() => {
    if (!data?.length) return null;

    return (
      <>
        <defs>
          {gradient && (
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="5%"
                stopColor={areaColor}
                stopOpacity={fillOpacity}
              />
              <stop offset="95%" stopColor={areaColor} stopOpacity={0.1} />
            </linearGradient>
          )}
        </defs>

        {showGrid && (
          <CartesianGrid
            strokeDasharray="2 4"
            stroke="#d1d5db"
            horizontal
            vertical
          />
        )}

        <XAxis
          dataKey={xAxisKey}
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fill: "#6b7280" }}
        />

        <YAxis
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fill: "#6b7280" }}
        />

        <Tooltip
          content={<CustomTooltip />}
          cursor={{ stroke: "#d1d5db", strokeWidth: 1, strokeDasharray: "4 4" }}
        />

        <Area
          type="monotone"
          dataKey={yAxisKey}
          stroke={areaColor}
          strokeWidth={strokeWidth}
          fill={gradient ? `url(#${gradientId})` : areaColor}
          fillOpacity={gradient ? 1 : fillOpacity}
        />
      </>
    );
  }, [
    data,
    xAxisKey,
    yAxisKey,
    areaColor,
    showGrid,
    strokeWidth,
    fillOpacity,
    gradient,
    gradientId,
    CustomTooltip,
  ]);

  const chartStates = useMemo(
    () => ({
      loading: (
        <div className="chart-loading">
          <i className="ti ti-loader-2 spin" />
          <span>Loading...</span>
        </div>
      ),
      error: (
        <div className="chart-error">
          <i className="ti ti-alert-circle" />
          <span>{error}</span>
        </div>
      ),
      empty: (
        <div className="chart-empty">
          <i className="ti ti-chart-area" />
          <span>No data available</span>
        </div>
      ),
    }),
    [error]
  );

  const renderChart = () => {
    if (loading) return chartStates.loading;
    if (error) return chartStates.error;
    if (!data?.length) return chartStates.empty;

    return (
      <RechartsAreaChart
        data={data}
        margin={{ top: 10, right: 30, left: 20, bottom: 5 }}>
        {chartElements}
      </RechartsAreaChart>
    );
  };

  return (
    <div className="area-chart-container">
      <div className="chart-wrapper" style={{ height: `${height}px` }}>
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default AreaChart;
